<template>
	<div
		v-scroll-container="{
			target: isMobileViewport ? '.js-scroll-wrapper-mobile' : '.js-scroll-wrapper',
			disableScrollShadow: false,
		}"
		class="highlight-tool"
		:class="{ 'is-mobile': isMobileViewport }"
	>
		<VideoCreateForm
			v-if="isCreatingVideo"
			:event-tags="selectedTags"
			:event-tag-graphics="selectedTagGraphics"
			modal-type="modal-md"
		/>

		<HighlightFormulaDebugger
			v-if="showFormulaDebugger"
			:sport-name="sportName"
			:formula-key="selectedAutoHighlightFormula"
			:initial-formula-config="currentFormulaConfigForDebugger"
			:all-event-tags="eventTagsWithoutClockTags"
			:selected-tags="selectedTags"
			@close-debugger="showFormulaDebugger = false"
			@debug-config-changed="onDebugConfigChanged"
		/>

		<div class="highlight-tool__col -left">
			<Tab
				icon-size="lg"
				class="desktop-nav"
				:active-tab="activeTab"
				:tabs="tabs"
				:hide-header="!isNarrowTaggingTool"
				@tab-change="handleTabChange"
			>
				<!-- tags -->
				<template #tab-details-tags>
					<div class="highlight-tool__main-content js-scroll-wrapper-mobile">
						<div class="l-asport-one-panel-section">
							<TagGroupsDropdown
								:options="tagGroupOptions"
								:value="selectedTagGroupOption"
								:enable-create="false"
								@input="onTagGroupSelectionChange"
							/>
						</div>
						<!-- Section HighlightIQ -->
						<div
							v-if="showHighlightIq"
							class="l-asport-one-panel-section -no-v-padding"
						>
							<Accordion
								:open="accordions.highlightIq.isOpen"
								:title="$I18n.trans('highlights.highlight_tool.iq.title')"
								:subtitle="$I18n.trans('highlights.highlight_tool.iq.subtitle')"
								:icon-hint="highlightIqTooltip"
								icon="info"
								@toggle="toggleAccordion('highlightIq')"
							>
								<Select
									dark-theme
									:placeholder="$I18n.trans('highlights.highlight_tool.select_formula')"
									:options="autoHighlightFormulas"
									:value="selectedAutoHighlightFormula"
									:searchable="false"
									:disabled="eventTagsWithoutClockTags.length === 0"
									@change="onAutoHighlightFormulaChange"
								/>
								<div class="u-mb-2">
									Clip count
								</div>
								<WheelSelect
									class="u-mb-6"
									:min="minWheelValue"
									:max="maxWheelValue"
									:value="wheelSelectValue"
									:disabled="!selectedAutoHighlightFormula"
									@input="handleWheelSelectInput"
								/>
								<HighlightIqWarning
									v-if="!highlightDetectionActive"
									:warning-type="highlightIqWarningType"
								/>
								<HighlightDetectionWidget
									v-if="highlightIqWarningType === 'missing-scores' || highlightDetectionActive"
									:hide-create-tags="true"
									class="l-asport-one-panel-h-padding mt-3"
									@completed="handleDetectionCompleted"
								/>
							</Accordion>
						</div>
						<div class="l-asport-one-panel-section">
							<HighlightTagList
								:checkbox-always-visible="true"
								:currently-previewing-tag="previewingTag"
								@selection-changed="handleSelectionChanged"
								@preview="handleTagPreview"
								@stop-preview="exitPreviewMode"
							/>
						</div>
					</div>
				</template>
				<template #tab-details-clips>
					<EventTagEdit
						v-if="isEditingSingleEventTag"
						:elapsed="timelineProgress"
						:hide-multiformat-editor="true"
					/>
					<HighlightClipsList
						v-else
						:selected-clips="selectedClips"
						:event-id="event.id"
						:current-clip="currentClip"
						@play="handleClipPlay"
						@remove="handleClipRemove"
						@edit="handleClipEdit"
					/>
				</template>
			</Tab>
		</div>

		<div
			v-adaptive-height="{ target: '.js-highlight-tool-player' }"
			class="highlight-tool__col -main"
		>
			<div
				v-if="showInstructions"
				class="highlight-tool__instructions"
				:class="[
					{
						'is-mobile': isMobileViewport,
					},
					`-count-${instructions.length}`,
				]"
			>
				<div
					v-for="(instruction, index) in instructions"
					:key="index"
					class="highlight-tool__instruction-item"
				>
					<div class="highlight-tool__instruction-number">
						0{{ index + 1 }}
					</div>
					<div class="highlight-tool__instruction-content">
						<h3 class="highlight-tool__instruction-title">
							{{ instruction.title }}
						</h3>
						<p class="highlight-tool__instruction-text">
							{{ instruction.text }}
						</p>
					</div>
				</div>
			</div>
			<div
				v-else
				class="highlight-tool__tags-list"
				:class="{ 'l-asport-one-panel-section': !isMobileViewport }"
			>
				<ErrorScreen
					v-if="hasErrorScreen"
					:screen="errorScreen"
				/>
				<div
					v-else
					class="highlight-tool__player-w"
				>
					<div
						ref="player-wrapper-container"
						class="highlight-tool__media-w"
						@click="isPlaying = !isPlaying"
					>
						<Player
							v-if="streamURL"
							ref="player"
							class="js-highlight-tool-player"
							:autoplay="false"
							:url="streamURL"
							:initial-volume="50"
							:was-muted-manually="wasMutedManually"
							:forced-duration="forcedDuration"
							:stream-lead-time-seconds="streamLeadTimeSeconds"
							@error="handleError"
							@error-clear="handleErrorClear"
							@mute="isMuted = true"
							@unmute="isMuted = false"
							@volume="volumeLevel = $event"
							@audio-tracks-update="handleAudioTracksUpdate"
							@audio-track-change="handleAudioTrackChange"
							@position="handlePlayerProgress"
						>
							<template #over>
								<HighlightAnimation
									v-if="canManageGraphics"
									:current-clip="currentClip"
									:selected-clips="selectedClips"
									:is-previewing-tag="isPreviewingTag"
									:event-id="event.id"
									:event-labels="eventLabels"
									:enabled-graphic-ids="enabledGraphicIds"
									:is-playing="isPlaying"
									:current-progress="playerProgress"
									@graphics-config-loaded="handleGraphicsConfigLoaded"
								/>
							</template>
						</Player>
					</div>
					<PlayerControls
						v-measure-width="{
							callback: onControlSizeChange,
							sizes: HIGHLIGHT_TOOL_CONTROL_SIZES_CONFIG,
						}"
						class="player-overlay__controls"
						:show-play-toggle="true"
						:show-share-button="false"
						:show-fullscreen="false"
						:show-time-display="true"
						:show-tag-navigation="!isEditingSingleEventTag && !isPreviewingTag && selectedClips.length > 1"
						:current-tag-number="selectedClips.length > 0 ? currentClipIndex + 1 : 0"
						:tags-count="selectedClips.length"
						:controls-full-width="!isMobileViewport"
						:is-playing="isPlaying"
						:volume-level="volumeLevel"
						:is-muted="isMuted"
						:size="controlSize"
						:audio-tracks="audioTracks"
						:current-audio-track-id="currentAudioTrackId"
						:show-timeline="!isEditingSingleEventTag"
						:show-border="false"
						:duration="previewingTag ? previewTagDuration : totalDurationSeconds"
						:position="previewingTag ? previewTagPosition : timelineProgress"
						@play="isPlaying = true"
						@pause="isPlaying = false"
						@set-volume="volumeLevel = $event"
						@mute-toggle="isMuted = !isMuted"
						@next-clip="nextClip"
						@prev-clip="prevClip"
						@seek="handleTimelineSeek"
					/>
				</div>
			</div>
			<div
				v-if="isEditingSingleEventTag && !showInstructions"
				class="l-asport-one-panel-section"
			>
				<Timeline
					ref="timeline"
					:event="event"
					mode="edit"
					:elapsed="timelineProgress"
					:duration="event.duration"
					:edited-tag="selectedEditTag"
					:show-zoom-bar="true"
					:is-paused="!isPlaying"
					:is-tagging-tool="true"
					:participants="transformedParticipants"
					@seek="timelineEditSeek"
					@pause="isPlaying = false"
				/>
			</div>
			<div
				v-if="!isEditingSingleEventTag && !showInstructions && canManageGraphics && Object.keys(graphicsByGroup).length > 0"
				class="l-asport-one-panel-section highlight-tool__settings"
			>
				<div
					v-for="(options, group) in graphicsByGroup"
					:key="group"
					class="highlight-tool__settings-item"
				>
					<MultiSelect
						v-if="options.length > 0"
						dark-theme
						:searchable="false"
						:placeholder="$I18n.trans(`highlights.highlight_tool.graphics.select_placeholder.${group}`)"
						:label="$I18n.trans(`highlights.highlight_tool.graphics.select_label.${group}`)"
						:options="options"
						:value="selectedGraphicIds[group]"
						:selected-inline="true"
						:enable-toggle-all="false"
						@change="value => handleGraphicSelection(group, value)"
					/>
				</div>
			</div>
			<div
				v-if="!isEditingSingleEventTag && !showInstructions"
				class="l-asport-one-panel-section highlight-tool__actions"
			>
				<div class="highlight-tool__actions-infos">
					{{ selectedClips.length }} {{ $I18n.trans_choice('commons.clip', selectedClips.length) }}
					{{ isMobileViewport ? '' : `| ${totalDuration}` }}
				</div>
				<div
					v-if="selectedClips.length > 0"
					class="highlight-tool__actions-buttons"
				>
					<Button
						:text="$I18n.trans('highlights.audio_detection.tagging.reset')"
						theme="dark"
						modifier="secondary"
						:size="isMobileViewport ? 'small' : 'medium'"
						@click="handleReset"
					/>
					<Button
						:text="$I18n.trans('commons.type_create', { type: $I18n.trans_choice('commons.video', 1) })"
						theme="dark"
						:size="isMobileViewport ? 'small' : 'medium'"
						@click="handleExport"
					/>
				</div>
			</div>
		</div>
		<div
			v-if="!isNarrowTaggingTool"
			class="highlight-tool__col -right"
		>
			<EventTagEdit
				v-if="isEditingSingleEventTag"
				:elapsed="timelineProgress"
				:hide-multiformat-editor="true"
			/>
			<HighlightClipsList
				v-else
				:selected-clips="selectedClips"
				:event-id="event.id"
				:current-clip="currentClip"
				@play="handleClipPlay"
				@remove="handleClipRemove"
				@edit="handleClipEdit"
			/>
		</div>
	</div>
</template>

<script>
import Accordion from '@components/accordion/Accordion';
import TagGroupsDropdown from '@videotools/js/one/components/TagCenter/TagGroupsDropdown';
import VisibilityIconMixin from '@videotools/js/mixins/visibility-icon';
import HighlightTagList from '@videotools/js/one/modules/Highlight/HighlightTagList.vue';
import HighlightClipsList from '@videotools/js/one/modules/Highlight/HighlightClipsList.vue';
import Player from '@components/player/Player.vue';
import PlayerControls from '@shared/components/PlayerControls/PlayerControls.vue';
import { showVideoCreateErrorToast } from '@videotools/js/one/components/Video/video-create-utils';
import VideoCreateForm from '@videotools/js/one/components/Video/VideoCreateForm';
import WheelSelect from '@shared/components/WheelSelect';
import Select from '@shared/components/Select';
import MultiSelect from '@shared/components/MultiSelect';
import Tab from '@components/tab';
import Button from '@shared/components/Button';
import Timeline from '@components/timeline';
import EventTagEdit from '@js/one/components/EventTagEdit/EventTagEdit';
import HighlightDetectionWidget from '@js/one/components/highlights/HighlightDetectionWidget';
import VMeasureWidth from '@shared/directives/v-measure-width';
import VScrollContainer from '@shared/directives/v-scroll-container';
import AdaptiveHeight from '@shared/directives/v-adaptive-height';
import { HIGHLIGHT_TOOL_CONTROL_SIZES, HIGHLIGHT_TOOL_CONTROL_SIZES_CONFIG } from '@shared/constants/highlightToolControlSizes';
import { mapActions, mapGetters } from 'vuex';
import { playerBus } from '@videotools/js/events';
import { getDuration } from '@shared/utils/timeUtils';
import { VideoCreateZeroDurationError } from '@videotools/js/store/modules/video-create';
import HighlightIqWarning from '@videotools/js/one/modules/Highlight/HighlightIqWarning.vue';
import {
	calculateExtraTagsCount,
	getDefaultClipCount,
	getSportFormulas,
	hasSportFormulas,
	hasTeamDistribution,
	selectTagsWithFormula,
} from '@videotools/js/one/modules/Highlight/HighlightIqService';
import { transformParticipants } from '@components/timeline/utils';
import HighlightAnimation from './HighlightAnimation.vue';
import { TagName } from '@shared/enums/TagName';

// Max values for the wheel select
const MAX_CLIPS = 50;
const DEFAULT_WHEEL_VALUE = 10;

export default {
	name:       'HighlightTool',
	components: {
		Tab,
		TagGroupsDropdown,
		Accordion,
		HighlightTagList,
		HighlightClipsList,
		Player,
		PlayerControls,
		Button,
		VideoCreateForm,
		WheelSelect,
		Select,
		MultiSelect,
		HighlightIqWarning,
		Timeline,
		EventTagEdit,
		HighlightAnimation,
		HighlightDetectionWidget,
		HighlightFormulaDebugger: () => import('@videotools/js/one/modules/Highlight/HighlightFormulaDebugger.vue'),
	},
	directives: {
		measureWidth:    VMeasureWidth,
		scrollContainer: VScrollContainer,
		adaptiveHeight:  AdaptiveHeight,
	},
	mixins: [VisibilityIconMixin],
	data() {
		return {
			accordions: {
				highlightIq: {
					isOpen: true,
				},
			},
			activeTab:              'tags',
			// Player
			playerWidth:            null,
			playerResizeObserver:   null,
			lastPlayerError:        null,
			errorTimeout:           -1,
			controlSize:            HIGHLIGHT_TOOL_CONTROL_SIZES.LG,
			// Player state
			isPlaying:              false,
			volumeLevel:            50,
			isMuted:                false,
			playerProgress:         0,
			HIGHLIGHT_TOOL_CONTROL_SIZES,
			HIGHLIGHT_TOOL_CONTROL_SIZES_CONFIG,
			autoHighlightFormulas:  [],
			wheelSelectValue:       DEFAULT_WHEEL_VALUE,
			highlightIqWarningType: '',
			showFormulaDebugger:    false,
			activeDebugConfig:      null,
			previewingTag:          null,
			previewTagDuration:     0,
			previewTagPosition:     0,
			previewStartTime:       0,
			availableGraphics:      [],
			selectedGraphicIds:     {}, // { dataModel: [{label,value}] }
		};
	},
	computed: {
		...mapGetters({
			event:                        'event/event',
			streamURL:                    'event/streamURL',
			streamLagTimeSeconds:         'event/streamLagTimeSeconds',
			streamLeadTimeSeconds:        'event/streamLeadTimeSeconds',
			eventTags:                    'event/tags',
			isEditingSingleEventTag:      'eventTagEdit/isSingleEdit',
			selectedEditTag:              'eventTagEdit/selectedTag',
			isLive:                       'event/isLive',
			hasErrorScreen:               'error/hasErrorScreen',
			errorScreen:                  'error/errorScreen',
			isNarrowTaggingTool:          'device/isNarrowTaggingTool',
			isMobileViewport:             'device/isMobileViewport',
			tagGroups:                    'tagGroups/tagGroups',
			selectedTagGroup:             'tagGroups/selectedTagGroup',
			wasMutedManually:             'player/wasMutedManually',
			audioTracks:                  'player/audioTracks',
			currentAudioTrackId:          'player/currentAudioTrackId',
			canManageGraphics:            'permissions/canManageGraphics',
			selectedClips:                'highlightTool/selectedClips',
			currentClip:                  'highlightTool/currentClip',
			currentClipIndex:             'highlightTool/currentClipIndex',
			isCreatingVideo:              'videoCreate/isActive',
			selectedAutoHighlightFormula: 'highlightTool/selectedAutoHighlightFormula',
			canUseHighlightIq:            'permissions/canUseHighlightIq',
			isHighlightsRunning:          'highlights/isRunning',
			highlightDetectionActive:     'highlights/isActive',
		}),
		eventTagsWithoutClockTags() {
			return this.eventTags.filter(tag => ![TagName.TIMING_CLOCK_START, TagName.TIMING_CLOCK_END, TagName.TIMING_CLOCK_SET].includes(tag.tag?.name));
		},
		forcedDuration() {
			if (this.isLive) {
				return this.event.durationSeconds - this.streamLagTimeSeconds;
			}
			return undefined;
		},
		highlightIqTooltip() {
			return `${this.$I18n.trans('highlights.highlight_tool.info.intro')}<br><br>
				<ol>
					<li><b>${this.$I18n.trans('highlights.highlight_tool.info.types.match_highlights.title')} </b><br>${this.$I18n.trans('highlights.highlight_tool.info.types.match_highlights.info')}</li>
					<li><b>${this.$I18n.trans('highlights.highlight_tool.info.types.condensed_game.title')} </b><br>${this.$I18n.trans('highlights.highlight_tool.info.types.condensed_game.info')}</li>
					<li><b>${this.$I18n.trans('highlights.highlight_tool.info.types.high_emotion.title')} </b><br>${this.$I18n.trans('highlights.highlight_tool.info.types.high_emotion.info')}</li>
					<li><b>${this.$I18n.trans('highlights.highlight_tool.info.types.tense_moments.title')} </b><br>${this.$I18n.trans('highlights.highlight_tool.info.types.tense_moments.info')}</li>
				</ol>`;
		},
		transformedParticipants() {
			return transformParticipants(this.event?.participants);
		},
		sportName() {
			return this.event?.sport?.name;
		},
		showHighlightIq() {
			return hasSportFormulas(this.sportName) && this.canUseHighlightIq;
		},
		maxWheelValue() {
			return Math.max(MAX_CLIPS, this.minWheelValue);
		},
		tabs() {
			const tabs = [
				{
					key:  'tags',
					icon: 'bullet-list',
				},
			];

			if (this.isNarrowTaggingTool) {
				tabs.push({
					key:  'clips',
					icon: 'video-playlist',
				});
			}

			return tabs;
		},
		tagGroupOptions() {
			return this.tagGroups.map(tag => ({
				id:    tag.id,
				label: tag.name,
				value: tag,
				icon:  this.visibilityIcon(tag.visibility),
			}));
		},
		selectedTagGroupOption() {
			return this.tagGroupOptions.find(tag => tag.id === this.selectedTagGroup.id);
		},
		// Player
		// Settings
		// audioChannelOptions() {
		// 	const options = [{ id: 'all', label: 'All' }];
		// 	this.audioTracks.forEach(track => {
		// 		options.push({
		// 			id:    track.id,
		// 			label: track.name,
		// 		});
		// 	});
		// 	return options;
		// },
		totalDuration() {
			const seconds = this.selectedClips.reduce((acc, clip) => acc + clip.durationSeconds, 0);
			return getDuration(seconds, { appendUnit: true });
		},
		totalDurationSeconds() {
			return this.isEditingSingleEventTag
				? this.event.duration
				: this.selectedClips.reduce((acc, clip) => acc + clip.durationSeconds, 0);
		},
		currentProgress() {
			if (!this.currentClip || this.selectedClips.length === 0) {
				return 0;
			}

			// Calculate total duration of clips before current clip
			const previousClipsDuration = this.selectedClips
				.slice(0, this.currentClipIndex)
				.reduce((acc, clip) => acc + clip.durationSeconds, 0);

			// Calculate progress in current clip
			const currentClipStart = this.currentClip.tag.timestamp - this.currentClip.tag.secondsBefore;
			const currentClipProgress = Math.max(0, this.playerProgress - currentClipStart);
			const totalProgress = previousClipsDuration + currentClipProgress;

			return Math.min(totalProgress, this.totalDurationSeconds);
		},
		timelineProgress() {
			// When editing a single tag, use the actual video progress
			// Otherwise use the calculated progress within the clips sequence
			return this.isEditingSingleEventTag ? this.playerProgress : this.currentProgress;
		},
		selectedTags() {
			return this.selectedClips.map(clip => clip.tag);
		},
		selectedTagGraphics() {
			const tagGraphics = [];

			this.selectedTags.forEach(tag => {
				// Look for matching graphics for the given tag
				this.availableGraphics
					.filter(graphic => {
						return this.enabledGraphicIds.includes(graphic.id) && (graphic.allTags === true || graphic.tagIds.includes(tag.tag.id));
					})
					.forEach(graphic => {
						tagGraphics.push({ eventTagId: tag.id, graphicId: graphic.id });
					});
			});

			return tagGraphics;
		},
		eventLabels() {
			return this.event.labels?.map(label => label.name) ?? [];
		},
		tagsWithScores() {
			return this.eventTagsWithoutClockTags.filter(tag => tag.iqScore);
		},
		currentFormulaConfigForDebugger() {
			if (!this.sportName || !this.selectedAutoHighlightFormula || !this.showHighlightIq) {
				return null;
			}
			const formulas = getSportFormulas(this.sportName);
			return formulas ? formulas[this.selectedAutoHighlightFormula] : null;
		},
		isPreviewingTag() {
			return this.previewingTag !== null;
		},
		showInstructions() {
			return this.selectedClips.length === 0 && !this.isPreviewingTag;
		},
		instructions() {
			const instructions = [];

			// No tags found
			if (this.eventTagsWithoutClockTags.length === 0) {
				instructions.push({
					title: this.$I18n.trans('highlights.highlight_tool.instructions.no_tags'),
					text:  this.$I18n.trans(this.showHighlightIq ? 'highlights.highlight_tool.instructions.no_tags_description_iq' : 'highlights.highlight_tool.instructions.no_tags_description_no_iq'),
				});

				return instructions;
			}

			if (this.showHighlightIq) {
				instructions.push({
					title: this.$I18n.trans('highlights.highlight_tool.instructions.iq_activate'),
					text:  this.$I18n.trans('highlights.highlight_tool.instructions.iq_description'),
				});
			}

			instructions.push({
				title: this.$I18n.trans('highlights.highlight_tool.instructions.choose_scenes'),
				text:  this.$I18n.trans(this.showHighlightIq ? 'highlights.highlight_tool.instructions.scenes_description' : 'highlights.highlight_tool.instructions.scenes_description_no_iq'),
			});

			instructions.push({
				title: this.$I18n.trans('highlights.highlight_tool.instructions.refine_video'),
				text:  this.$I18n.trans('highlights.highlight_tool.instructions.refine_description'),
			});

			instructions.push({
				title: this.$I18n.trans('highlights.highlight_tool.instructions.share_highlight'),
				text:  this.$I18n.trans('highlights.highlight_tool.instructions.share_description'),
			});

			return instructions;
		},
		graphicsByGroup() {
			const grouped = {};
			this.availableGraphics.forEach(graphic => {
				if (!grouped[graphic.group]) {
					grouped[graphic.group] = [];
				}
				grouped[graphic.group].push({
					value: graphic.id,
					label: graphic.name,
				});
			});
			return grouped;
		},
		enabledGraphicIds() {
			return Object.values(this.selectedGraphicIds).filter(group => group !== null).flatMap(group => group.map(graphic => graphic.value));
		},
		currentFormulaConfig() {
			if (!this.sportName || !this.selectedAutoHighlightFormula || !this.showHighlightIq) {
				return null;
			}
			const formulas = getSportFormulas(this.sportName);
			return formulas ? formulas[this.selectedAutoHighlightFormula] : null;
		},
		minWheelValue() {
			if (!this.selectedAutoHighlightFormula) {
				return 0;
			}

			// Use the active debug config if available, otherwise use the current formula config
			const effectiveConfig = this.showFormulaDebugger && this.activeDebugConfig
				? this.activeDebugConfig
				: this.currentFormulaConfig;

			if (!effectiveConfig) {
				return 0;
			}

			return calculateExtraTagsCount(this.eventTagsWithoutClockTags, effectiveConfig, this.sportName, this.event);
		},
	},
	watch: {
		isPlaying(isPlaying) {
			if (isPlaying) {
				this.$refs.player.play();
			} else {
				this.$refs.player.pause();
			}
		},
		volumeLevel(volumeLevel) {
			this.$refs.player.setVolume(volumeLevel);
		},
		isMuted(isMuted) {
			if (isMuted) {
				this.$refs.player.mute();
			} else {
				this.$refs.player.unmute();
			}
		},
		currentClipIndex() {
			if (this.selectedClips.length === 0) {
				return;
			}

			// Only auto-seek if we're not in a timeline seek operation
			if (!this.$refs.player.isSeeking && !this.isEditingSingleEventTag) {
				this.seekToClip(this.currentClip.tag);
			}
		},
		isEditingSingleEventTag(newVal) {
			if (newVal) {
				this.$nextTick(() => {
					this.$refs.timeline?.adaptToEditTagMode();
				});
			}
		},
		isNarrowTaggingTool() {
			this.$nextTick(() => {
				this.activeTab = this.tabs[0].key;
			});
		},
		sportName: {
			immediate: true,
			handler() {
				this.initializeHighlightIqFormulas();
			},
		},
		playerProgress(progress) {
			// Update preview position if in preview mode
			if (this.isPreviewingTag) {
				const previewProgress = progress - this.previewStartTime;
				if (previewProgress >= 0) {
					// Keep the position within the preview bounds for the UI
					this.previewTagPosition = Math.min(previewProgress, this.previewTagDuration);

					// Only exit preview mode when reaching the end AND playing (not manual seeking)
					if (previewProgress >= this.previewTagDuration && this.isPlaying) {
						this.exitPreviewMode();
						this.isPlaying = false;
					}
				} else if (progress < this.previewStartTime) {
					// If player is before the preview start (can happen with race conditions)
					// Seek back to the proper position
					this.$refs.player.seek(this.previewStartTime);
				}
			}
		},
	},
	mounted() {
		this.handlePlayerResize();
		this.initializeHighlightIqFormulas();

		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('debugFormulas')) {
			this.showFormulaDebugger = true;
		}
	},
	methods: {
		...mapActions({
			setPlayerError:                  'error/setPlayerError',
			setSelectedTagGroup:             'tagGroups/setSelected',
			setAudioTracks:                  'player/setAudioTracks',
			setCurrentAudioTrackId:          'player/setCurrentAudioTrackId',
			updateSelectedClips:             'highlightTool/updateSelectedClips',
			updateHighlightIqSelectedClips:  'highlightTool/updateHighlightIqSelectedClips',
			nextClip:                        'highlightTool/nextClip',
			prevClip:                        'highlightTool/prevClip',
			goToClip:                        'highlightTool/goToClip',
			prepareVideoCreate:              'videoCreate/prepare',
			editEventTag:                    'eventTagEdit/editEventTag',
			clearEditSelection:              'eventTagEdit/clearSelection',
			setSelectedAutoHighlightFormula: 'highlightTool/setSelectedAutoHighlightFormula',
			loadEventTags:                   'event/loadEventTags',
		}),
		initializeHighlightIqFormulas() {
			if (!this.showHighlightIq) {
				this.autoHighlightFormulas = [];
				if (this.selectedAutoHighlightFormula) {
					this.setSelectedAutoHighlightFormula(null);
				}
				return;
			}

			const formulas = getSportFormulas(this.sportName);
			if (!formulas) {
				return;
			}

			// Create formula options, excluding baseConfig
			const formulaOptions = Object.keys(formulas)
				.filter(key => key !== 'baseConfig')
				.map(key => ({
					value: key,
					label: this.$I18n.trans(`highlights.highlight_tool.info.types.${key}.title`),
				}));

			this.autoHighlightFormulas = formulaOptions;

			// Only set to null if not already set in the store
			if (!this.selectedAutoHighlightFormula) {
				this.setSelectedAutoHighlightFormula(null);
			}
		},
		handleTabChange(tabKey) {
			this.activeTab = tabKey;
		},
		onTagGroupSelectionChange(tagGroup) {
			this.setSelectedTagGroup(tagGroup);
		},
		toggleAccordion(accordion) {
			this.accordions[accordion].isOpen = !this.accordions[accordion].isOpen;
		},
		// Player
		handlePlayerResize() {
			this.recomputePlayerWidth();

			if (window.ResizeObserver && this?.$refs['player-wrapper-container']) {
				const observer = new ResizeObserver(this.recomputePlayerWidth);
				observer.observe(this?.$refs['player-wrapper-container']);
				this.playerResizeObserver = observer;
			} else {
				window.addEventListener('resize', () => this.recomputePlayerWidth, true);
			}
		},
		recomputePlayerWidth() {
			this.$nextTick(() => {
				setTimeout(() => {
					requestAnimationFrame(() => {
						this.playerWidth = this?.$refs['player-wrapper-container']?.clientWidth;
					});
				}, 50);
			});
		},
		handleError(error) {
			if (error?.type !== this.lastPlayerError?.type) {
				clearTimeout(this.errorTimeout);
				this.errorTimeout = setTimeout(() => {
					this.setPlayerError(error);
				}, 3000);
			}

			this.lastPlayerError = error;
		},
		handleErrorClear() {
			clearTimeout(this.errorTimeout);
			this.setPlayerError(null);
		},
		onControlSizeChange(newSize) {
			this.controlSize = newSize;
		},
		handleAudioTracksUpdate(value) {
			this.setAudioTracks(value);
		},
		handleAudioTrackChange(value) {
			this.setCurrentAudioTrackId(value);
		},
		handleSelectionChanged(selectedItems) {
			if (this.isEditingSingleEventTag) {
				this.clearEditSelection();
			}
			this.updateSelectedClips(selectedItems);
		},
		handleClipPlay(clip) {
			this.exitPreviewMode();

			this.goToClip(clip);
			this.seekToClip(clip.tag);
		},
		handleClipRemove(clip) {
			const newSelectedClips = this.selectedClips.filter(c => c.id !== clip.id);
			this.updateSelectedClips(newSelectedClips);
		},
		handleClipEdit(clip) {
			this.goToClip(clip);
			this.seekToClip(clip.tag, false);
			this.editEventTag(clip.tag);

			// Allow time for the Timeline to be mounted and edit mode to be set
			this.$nextTick(() => {
				this.$refs.timeline?.adaptToEditTagMode();
			});
		},
		handlePlayerProgress(progress) {
			const playerProgress = Math.max(0, progress);
			this.playerProgress = playerProgress;

			// used for ClipListTile to highlight the correct clip
			playerBus.emitProgress(playerProgress);

			// If in preview mode, don't execute the normal clip navigation logic
			if (this.isPreviewingTag || this.isEditingSingleEventTag) {
				// Preview logic is handled in the playerProgress watcher
				return;
			}

			if (!this.currentClip) {
				return;
			}

			// Check if we're at the end of the last clip
			if (this.currentClipIndex === this.selectedClips.length - 1 &&
					playerProgress > (this.currentClip.timestamp + this.currentClip.tag.secondsAfter)) {
				this.isPlaying = false;
				this.$refs.player.pause();
				return;
			}

			if (playerProgress > (this.currentClip.timestamp + this.currentClip.tag.secondsAfter)) {
				this.nextClip();
			} else if (playerProgress < this.currentClip.tag.timestamp - this.currentClip.tag.secondsBefore) {
				this.seekToClip(this.currentClip.tag);
			}
		},
		seekToClip(tag, play = true) {
			const start = Math.max(0, tag.timestamp - tag.secondsBefore);
			this.$refs.player.seek(start);
			if (play) {
				this.$refs.player.play();
				this.isPlaying = true;
			} else {
				this.isPlaying = false;
				this.$refs.player.pause();
			}
		},
		handleReset() {
			this.updateSelectedClips([]);
			this.updateHighlightIqSelectedClips([]);

			// Reset the highlight formula selection
			this.setSelectedAutoHighlightFormula(null);

			if (this.selectedAutoHighlightFormula) {
				this.wheelSelectValue = DEFAULT_WHEEL_VALUE;
			}

			this.highlightIqWarningType = '';
		},
		async handleExport() {
			try {
				await this.prepareVideoCreate(this.selectedTags);
			} catch (e) {
				if (e instanceof VideoCreateZeroDurationError) {
					showVideoCreateErrorToast(e);
				} else {
					throw e;
				}
			}
		},
		handleTimelineSeek(seekPosition) {
			// If in preview mode, adjust the seek position relative to preview
			if (this.isPreviewingTag) {
				// Calculate the absolute position in the video
				if (seekPosition >= 0 && seekPosition <= this.previewTagDuration) {
					const absolutePosition = this.previewStartTime + seekPosition;
					this.$refs.player.seek(absolutePosition);
					return;
				} else {
					// If seeking outside preview bounds, exit preview mode
					this.exitPreviewMode();
				}
			}

			if (this.selectedClips.length === 0) {
				return;
			}

			let accumulatedDuration = 0;
			for (let i = 0; i < this.selectedClips.length; i++) {
				const clip = this.selectedClips[i];
				const clipEnd = accumulatedDuration + clip.durationSeconds;

				if (seekPosition <= clipEnd) {
					// Found the clip to seek to
					const clipRelativePosition = seekPosition - accumulatedDuration;
					const absolutePosition = clip.tag.timestamp - clip.tag.secondsBefore + clipRelativePosition;

					// Update the current clip in store without auto-seeking
					this.goToClip(clip.tag);

					// Prevent the watch handler from auto-seeking
					this.$nextTick(() => {
						this.$refs.player.seek(absolutePosition);
						if (this.isPlaying) {
							this.$refs.player.play();
						}
					});
					return;
				}

				accumulatedDuration = clipEnd;
			}

			// If we get here, seek to the last clip
			const lastClip = this.selectedClips[this.selectedClips.length - 1];
			this.goToClip(lastClip.tag);
			const lastClipEnd = lastClip.tag.timestamp + lastClip.tag.secondsAfter;
			this.$nextTick(() => {
				this.$refs.player.seek(lastClipEnd);
				if (this.isPlaying) {
					this.$refs.player.play();
				}
			});
		},
		timelineEditSeek(time) {
			// This handles seeking in the Timeline component when in edit mode
			this.$refs.player.seek(time);

			// If we're playing, keep playing
			if (this.isPlaying) {
				this.$refs.player.play();
			}
		},
		handleWheelSelectInput(value) {
			if (!this.selectedAutoHighlightFormula) {
				return;
			}

			this.wheelSelectValue = value;
			this.applyHighlightFormula();
		},
		onAutoHighlightFormulaChange(formula) {
			this.setSelectedAutoHighlightFormula(formula);

			// Set the default clip count for this formula
			if (formula) {
				const defaultValue = getDefaultClipCount(this.sportName, formula);

				// Get the formula config to calculate minimum value
				const formulas = getSportFormulas(this.sportName);
				const formulaConfig = formulas ? formulas[formula] : null;

				// Calculate minimum value
				const minValue = calculateExtraTagsCount(this.eventTagsWithoutClockTags, formulaConfig, this.sportName, this.event);

				// Ensure the value is not below the minimum
				this.wheelSelectValue = Math.max(defaultValue, minValue);
				this.applyHighlightFormula();
			} else {
				// Reset selection if formula is null
				this.updateSelectedClips([]);
				this.updateHighlightIqSelectedClips([]);
				this.highlightIqWarningType = '';
			}
		},
		onDebugConfigChanged(newDebugConfig) {
			this.activeDebugConfig = newDebugConfig;

			// Ensure wheel select value is not below the minimum
			this.wheelSelectValue = Math.max(this.wheelSelectValue, this.minWheelValue);

			// When the debugger's config changes, re-apply the formula selection
			if (this.selectedAutoHighlightFormula) { // Only apply if a formula is actually selected
				this.applyHighlightFormula();
			}
		},
		applyHighlightFormula() {
			if (!this.selectedAutoHighlightFormula || !this.showHighlightIq) {
				this.highlightIqWarningType = '';
				return;
			}

			// Reset warning
			this.highlightIqWarningType = '';

			// If wheel value is 0, just clear selections without showing any warning
			if (this.wheelSelectValue === 0) {
				this.updateSelectedClips([]);
				this.updateHighlightIqSelectedClips([]);
				return;
			}

			// Check if none of the tags have an audioScore
			const hasTagsWithAudioScore = this.tagsWithScores.some(tag => tag.iqScore?.audioScore > 0);

			if (!hasTagsWithAudioScore) {
				this.highlightIqWarningType = 'missing-scores';
				return;
			}

			const formulaType = this.selectedAutoHighlightFormula;
			if (!formulaType) {
				return;
			}

			const limitType = 'clips';
			const limitValue = this.wheelSelectValue;

			// Determine which formula configuration to use
			let effectiveFormulaConfig = null;
			if (this.showFormulaDebugger && this.activeDebugConfig) {
				effectiveFormulaConfig = this.activeDebugConfig;
			} else {
				// Standard behavior: get config from HighlightIqService
				const formulas = getSportFormulas(this.sportName);
				effectiveFormulaConfig = formulas ? formulas[formulaType] : null;
			}

			if (!effectiveFormulaConfig) {
				console.warn(`No formula config found for ${formulaType} in ${this.sportName}`);
				this.updateSelectedClips([]);
				this.updateHighlightIqSelectedClips([]);
				return;
			}

			// Show a warning if formula prefers team alternation but doesn't have enough distribution
			// But still allow the formula to run - it will prioritize extraTags before trying alternating teams
			if (effectiveFormulaConfig?.balanceTeams === true && !hasTeamDistribution(this.eventTagsWithoutClockTags)) {
				this.highlightIqWarningType = 'not-enough-team-tags';
				// We don't return here, we still proceed with tag selection
			}

			// Select tags based on formula
			const sortedEventTags = this.eventTagsWithoutClockTags.sort((a, b) => a.timestamp - b.timestamp);
			const selectedTags = selectTagsWithFormula(
				sortedEventTags,
				this.event,
				formulaType, // formulaType is still needed for some internal logic in selectTags, even with custom config
				limitType,
				limitValue,
				effectiveFormulaConfig // Pass the determined config (debug or standard)
			).sort((a, b) => a.timestamp - b.timestamp);

			const shouldLogHighlightIqSelection = (() => {
				try {
					return new URLSearchParams(window.location.search).has('debugFormulas');
				} catch (error) {
					return false;
				}
			})();

			if (shouldLogHighlightIqSelection) {
				console.groupCollapsed(`[HighlightIQ] apply formula=${formulaType} limit=${limitType}:${limitValue}`);
				console.log('input_tags', {
					totalTags:  sortedEventTags.length,
					tagsWithIq: sortedEventTags.filter(tag => !!tag.iqScore).length,
					config:     effectiveFormulaConfig,
				});
				console.table(selectedTags.map(tag => ({
					id:        tag.id,
					timestamp: tag.timestamp,
					name:      tag.tag?.name || tag.name,
					teamId:    tag.teamId,
					score:     tag.calculatedScore,
					reason:    tag.selectionReason,
				})));
				console.groupEnd();
			}

			// We still show a warning for non-zero limits that produce empty results
			if (selectedTags.length === 0 && limitValue > 0 && this.highlightIqWarningType !== 'not-enough-team-tags' && this.highlightIqWarningType !== 'missing-scores') {
				this.highlightIqWarningType = 'not-enough-tags';
				return;
			}

			// Create clips objects from the selected tags
			const selectedClips = selectedTags.map(tag => ({
				id:              tag.id,
				tag,
				name:            tag.name,
				time:            tag.formattedTimestamp,
				duration:        tag.formattedDuration,
				durationSeconds: tag.secondsAfter + tag.secondsBefore,
				imageUrl:        tag.imageUrl,
				player:          tag.player,
				team:            tag.teamName,
				timestamp:       tag.timestamp,
				participantName: tag.participantName,
				action:          tag.name,
				gameMinute:      tag.gameTime?.gameMinute,
				selectionReason: tag.selectionReason,
			}));

			// Update selected clips
			this.updateSelectedClips(selectedClips);
			this.updateHighlightIqSelectedClips(selectedClips);
		},
		handleTagPreview(tag) {
			// Exit current preview if there is one
			this.exitPreviewMode();

			// Enter preview mode
			this.previewingTag = tag;
			this.previewTagDuration = tag.tag.secondsBefore + tag.tag.secondsAfter;
			this.previewTagPosition = 0;

			// Set the start time
			const start = Math.max(0, tag.tag.timestamp - tag.tag.secondsBefore);
			this.previewStartTime = start;

			// Wait for DOM update if we were in instructions mode
			this.$nextTick(() => {
				// Now the player should be available since instructions are hidden
				if (this.$refs.player) {
					// Add a slight delay to ensure the player is fully mounted and ready
					// when coming from instructions mode (selectedClips empty)
					setTimeout(() => {
						this.$refs.player.seek(start);
						this.$refs.player.play();
						this.isPlaying = true;
					}, 50);
				}
			});
		},
		exitPreviewMode() {
			if (!this.isPreviewingTag) {
				return;
			}

			this.previewingTag = null;
			this.previewTagDuration = 0;
			this.previewTagPosition = 0;
			this.previewStartTime = 0;

			// Return to normal video position if there are selected clips
			if (this.selectedClips.length > 0 && this.currentClip) {
				this.seekToClip(this.currentClip.tag, false);
			}
		},
		handleGraphicsConfigLoaded(config) {
			this.availableGraphics = config;

			// Initialize selectedGraphicIds with null for each dataModel
			const dataModels = [...new Set(config.map(g => g.group))];
			dataModels.forEach(dataModel => {
				this.$set(this.selectedGraphicIds, dataModel, null);
			});
		},
		handleGraphicSelection(dataModel, graphicIds) {
			this.$set(this.selectedGraphicIds, dataModel, graphicIds);
		},
		async handleDetectionCompleted() {
			this.highlightIqWarningType = '';
			await this.loadEventTags({ id: this.event.id });
			this.applyHighlightFormula();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.highlight-tool {
	display: flex;
	flex-direction: row;
	justify-content: center;
	gap: $sp2;
	height: 100%;

	&.is-mobile {
		flex-direction: column;
	}
}

.highlight-tool__col {
	display: flex;
	flex-direction: column;
	gap: $sp2;
	height: 100%;

	&.-left,
	&.-right {
		width: 475px;
		flex-shrink: 0;

		.highlight-tool.is-mobile & {
			width: 100%;
			height: auto;
			flex-grow: 1;
			flex-shrink: 1;
		}
	}

	&.-main {
		flex-grow: 1;

		.highlight-tool.is-mobile & {
			order: -1;
			height: auto;
			flex-grow: 0;
		}
	}
}

.highlight-tool__player-w {
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
}

.highlight-tool__media-w {
	width: 100%;
}

.highlight-tool__settings {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	gap: $sp3;
}

.highlight-tool__settings-item {
	max-width: 50%;
	flex-grow: 1;
}

.highlight-tool__actions {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
}

// .highlight-tool__actions-infos {}

.highlight-tool__actions-buttons {
	display: flex;
	flex-direction: row;
	gap: $sp4;
}

.highlight-tool__main-content {
	display: flex;
	flex-direction: column;
	height: 100%;
	gap: $sp2;

	.highlight-tool.is-mobile & {
		overflow-y: auto;
		height: auto;
	}
}

.highlight-tool__instructions {
	position: relative;
	display: grid;
	grid-template-columns: repeat(1, 1fr);
	gap: $sp4;
	padding: $sp6;
	background-color: var(--one-panel-background-color);
	border-radius: $border-radius--sm;
	height: 100%;
	align-items: center;
	justify-content: center;

	@include size(sm-up) {
		grid-template-columns: repeat(2, 1fr);
		gap: $sp6;
	}

	@media (min-width: 1900px) {
		grid-template-columns: repeat(4, 1fr);

		&::before,
		&::after {
			content: '';
			display: block;
			position: absolute;
			top: 25%;
			left: $sp6;
			right: $sp6;
			height: 1px;
			background-color: var(--color-grey-600);
		}

		&::after {
			top: 72%;
		}
	}

	&.is-mobile {
		grid-template-columns: 1fr;
		padding: $sp6 $sp4;
	}

	&.-count-1 {
		@include size(sm-up) {
			grid-template-columns: repeat(1, 1fr);
			gap: $sp6;
		}

		@media (min-width: 1900px) {
			grid-template-columns: repeat(1, 1fr);
		}
	}

	&.-count-3 {
		@include size(sm-up) {
			grid-template-columns: repeat(1, 1fr);
			gap: $sp6;
		}

		@media (min-width: 1900px) {
			grid-template-columns: repeat(3, 1fr);
		}
	}
}

.highlight-tool__instruction-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 0 $sp2;

	.highlight-tool__instructions:not(.is-mobile) & {
		min-height: 240px;
		justify-content: flex-start;
	}

	.highlight-tool__instructions.is-mobile & {
		flex-direction: row;
		text-align: left;
		align-items: flex-start;
		justify-content: flex-start;
		gap: $sp4;
		padding: 0;
	}
}

.highlight-tool__instruction-number {
	font-size: 100px;
	font-weight: 600;
	line-height: 1;
	color: var(--color-grey-600);
	margin-bottom: $sp3;

	.highlight-tool__instructions.is-mobile & {
		font-size: 40px;
		margin-bottom: 0;
		min-width: 80px;
	}
}

.highlight-tool__instruction-content {
	display: flex;
	flex-direction: column;
	gap: $sp2;
	max-width: 100%;
	text-align: center;

	.highlight-tool__instructions.is-mobile & {
		text-align: left;
	}
}

.highlight-tool__instruction-title {
	font-size: 18px;
	font-weight: bold;
	margin-bottom: $sp2;
	color: var(--color-white);

	.highlight-tool__instructions.is-mobile & {
		font-size: 14px;
		margin-bottom: $sp1;
	}
}

.highlight-tool__instruction-text {
	font-size: 14px;
	color: var(--color-grey-300);
	margin: 0;
	line-height: 1.4;
	text-align: center;

	.highlight-tool__instructions.is-mobile & {
		text-align: left;
		font-size: 12px;
	}
}
</style>
